import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { getTerms } from "@/data/termsContent";
import { useSiteSettings } from "@/lib/useSiteSettings";

function Block({ b }) {
  if (typeof b === "string") return <p className="text-sm leading-relaxed text-zinc-600">{b}</p>;
  return (
    <div>
      {b.lead && <p className="text-sm leading-relaxed text-zinc-600">{b.lead}</p>}
      <ul className="mt-1.5 list-disc space-y-1 pl-5 text-sm leading-relaxed text-zinc-600">
        {b.list.map((li, i) => <li key={i}>{li}</li>)}
      </ul>
    </div>
  );
}

export default function Terms() {
  const settings = useSiteSettings();
  // Render the version that matches the ACTIVE backend policy (default v1.0).
  const activeVersion = settings?.policies?.terms?.version || "1.0";
  const terms = getTerms(activeVersion);
  return (
    <div className="min-h-screen bg-[#faf8f3]" data-testid="terms-page">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-12">
        <Link to="/" data-testid="terms-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <div className="mt-5 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-zinc-900 text-white"><FileText className="h-5 w-5" /></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-900 sm:text-3xl">Nearfluence Terms of Service</h1>
            <p className="text-sm text-zinc-500">Version {terms.version} · Effective {terms.effective}</p>
          </div>
        </div>
        <p className="mt-4 rounded-xl bg-white p-4 text-sm leading-relaxed text-zinc-600 ring-1 ring-zinc-100">
          These Terms govern use of the Nearfluence platform by businesses and content creators. By creating, activating or continuing to use a Nearfluence account, you agree to these Terms.
        </p>

        <Accordion type="multiple" className="mt-5 space-y-2.5">
          {terms.sections.map((s) => (
            <AccordionItem key={s.id} value={s.id} data-testid={`terms-section-${s.id}`} className="rounded-2xl border border-zinc-200 bg-white px-4">
              <AccordionTrigger className="py-4 text-left text-[15px] font-semibold text-zinc-900 hover:no-underline">{s.title}</AccordionTrigger>
              <AccordionContent className="pb-4">
                <div className="space-y-2.5">{s.blocks.map((b, i) => <Block key={i} b={b} />)}</div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="mt-6 text-center text-xs text-zinc-400">
          See also our <Link to="/privacy" className="font-medium text-emerald-700 hover:underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
